import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, FileText, Truck, Ban, Layers, Tag, Check, Upload, Filter, ShieldAlert, Database } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { ChannelIcon } from '../components/ChannelIcon'
import { useActiveTenant } from '../contexts/tenant'
import { useAuth } from '../contexts/auth'
import { usePermissions } from '../contexts/permissions'
import { linkRastreio } from '@wsfull/shared'

type Label = { status: string; trackingCode: string | null } | null
type Order = {
  id: string; wsCode: string | null; orderNumber: string; erpNumber: string | null
  nfNumber: string | null; nfSeries: string | null; nfKey: string | null; status: string
  customerName: string | null; carrier: string | null; originChannel: string | null
  emissionDate: string | null; shippedAt: string | null; confirmedAt: string | null; createdAt: string
  label: Label; marketplace: boolean; pronto: boolean
  hasLabelDoc: boolean; hasDanfeDoc: boolean
  separacao?: { id: string; reference: string | null; status: string } | null
}

const orderStatusLabel: Record<string, string> = {
  pending: 'Pendente', imported: 'Importado', awaiting_label: 'Aguardando etiqueta',
  reserved: 'Liberado', picking: 'Em separação', packed: 'Embalado', shipped: 'Expedido',
  invoiced: 'Faturado', divergent: 'Divergente', returned: 'Devolvido', cancelled: 'Cancelado',
}
const orderStatusVariant: Record<string, 'default' | 'info' | 'warning' | 'success' | 'danger'> = {
  imported: 'default', awaiting_label: 'warning', reserved: 'info', picking: 'info',
  packed: 'info', shipped: 'success', invoiced: 'success', divergent: 'danger',
  returned: 'danger', cancelled: 'danger', pending: 'default',
}

const labelInfo = (label: Label): { text: string; variant: 'default' | 'info' | 'warning' | 'success' | 'danger' } => {
  if (!label) return { text: 'Sem etiqueta', variant: 'warning' }
  const map: Record<string, { text: string; variant: 'default' | 'info' | 'warning' | 'success' | 'danger' }> = {
    pending: { text: 'Pendente', variant: 'default' },
    authorized: { text: 'Autorizada', variant: 'success' },
    generated: { text: 'Gerada', variant: 'info' },
    printed: { text: 'Impressa', variant: 'info' },
    posted: { text: 'Postada', variant: 'info' },
    delivered: { text: 'Entregue', variant: 'success' },
    canceled: { text: 'Cancelada', variant: 'danger' },
  }
  return map[label.status] ?? { text: label.status, variant: 'default' }
}

// A série da NF está embutida na chave (44 díg.): posições 23-25. Deriva quando
// o pedido antigo não tem o campo nfSeries preenchido.
function nfSerie(o: { nfSeries: string | null; nfKey: string | null }): string | null {
  if (o.nfSeries) return o.nfSeries
  if (o.nfKey && o.nfKey.length === 44) {
    const s = Number(o.nfKey.slice(22, 25))
    return Number.isFinite(s) ? String(s) : null
  }
  return null
}

// Link de rastreio: a regra mora em @wsfull/shared (linkRastreio) — a API monta
// o mesmo link no CSV do rastreio em lote, e dois lugares montando URL de jeitos
// diferentes é o mesmo pedido com dois links.
const trackingUrl = (carrier: string | null, code: string) => linkRastreio(carrier, code)

// CONFLITO de etiqueta: o pedido tem arquivo anexado E envio vivo no Melhor
// Envio. A impressa é a anexada, e a do ME fica sem uso — se forem etiquetas
// diferentes, o pacote viaja com um rastreio e o cliente acompanha outro.
// Mesma regra do painel da onda; aqui ela precisa APARECER na lista, senão o
// filtro isola pedidos que a tela mostra como qualquer outro.
// Onda em que o pedido está. "Em separação" sozinho não diz EM QUAL — e é a
// primeira pergunta de quem procura um pedido que sumiu do fluxo. Clica e vai
// para a tela de Separação.
function OndaTag({ o, navigate }: { o: Order; navigate: (to: string) => void }) {
  if (!o.separacao) return null
  const estornada = o.separacao.status === 'cancelled'
  return (
    <button
      onClick={(e) => { e.stopPropagation(); navigate('/separacao-arquivo') }}
      title={estornada ? 'Onda estornada — o pedido voltou para a fila' : `Onda ${o.separacao.status}`}
      className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold ${
        estornada ? 'bg-gray-100 text-gray-400 line-through' : 'bg-violet-100 text-violet-700 hover:bg-violet-200'}`}>
      {o.separacao.reference ?? o.separacao.id.slice(0, 8)}
    </button>
  )
}

const temConflito = (o: Order) =>
  o.hasLabelDoc && !!o.label && o.label.status !== 'canceled' && o.label.status !== 'pending'

type Ponta = {
  id: string; pedido: string; nf: string | null; canal: string; status: string
  shippedAt: string | null; autorizadaEm: string | null; postadaEm: string | null
  rastreio: string | null; detalhe: string
}
type Auditoria = {
  analisados: number; pedidosComPonta: number
  grupos: { tipo: string; titulo: string; explica: string; total: number; pedidos: Ponta[] }[]
}

const ETIQUETA_LABEL: Record<string, string> = {
  '': 'todas', com: 'com etiqueta', limpa: 'com etiqueta, sem conflito',
  conflito: 'só conflito (anexo + Melhor Envio)', sem: 'sem etiqueta',
  'sem-postagem': 'expedido sem postagem (+3 dias, Melhor Envio)',
  liberados: 'liberados sem etiqueta (onda própria)',
  dispensa: 'saem sem etiqueta (manual + liberados)',
}

export function OrdersPage() {
  const tenantId = useActiveTenant()
  const { user } = useAuth()
  const { can } = usePermissions()
  const isAgent = user?.role === 'agent' // separador: só vê separar/DANFE, sem financeiro/expedição
  // Cliente é SOMENTE LEITURA: vê pedidos e status, sem nenhum botão de ação.
  const canEdit = user?.role !== 'client' && can('orders', 'edit')
  const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const [q, setQ] = useState('')
  // Default: 'authorized' (etiquetas prontas pra trabalhar). '' = a despachar · 'gone' = saiu
  // Etapa de expedição (filtro principal). Padrão: "a enviar" (com NF, não expedido).
  const [etapa, setEtapa] = useState('a-enviar')
  const [soPendentes, setSoPendentes] = useState(false) // só os que faltam etiqueta/doc
  // Filtro de etiqueta. Além de "com" e "sem", isola o caso que dá problema na
  // impressão: o pedido tem etiqueta anexada E envio no Melhor Envio. A impressa
  // é a anexada; se forem etiquetas diferentes, o pacote viaja com um rastreio e
  // o cliente acompanha outro. "Sem conflito" é a fila limpa para o dia a dia.
  const [etiqueta, setEtiqueta] = useState<'' | 'com' | 'limpa' | 'conflito' | 'sem' | 'sem-postagem' | 'liberados' | 'dispensa'>('')
  const [danfeId, setDanfeId] = useState<string | null>(null)
  const [danfeErr, setDanfeErr] = useState<string | null>(null)
  // Guarda os OBJETOS selecionados (não só ids) — assim a fixação persiste mesmo
  // trocando busca/filtro/página, quando o pedido não está na carga atual.
  const [selected, setSelected] = useState<Map<string, Order>>(new Map())
  const [shipping, setShipping] = useState(false)
  const [shipMsg, setShipMsg] = useState<string | null>(null)
  const [comprando, setComprando] = useState(false)
  // Auditoria de pontas abertas: pedidos cujo estado se contradiz. Roda sob
  // demanda — varre todo o período, e ninguém quer isso a cada carregamento.
  const [auditoria, setAuditoria] = useState<{ aberto: boolean; carregando: boolean; erro: string; res: Auditoria | null; grupo: string | null }>(
    { aberto: false, carregando: false, erro: '', res: null, grupo: null })

  async function auditar() {
    setAuditoria(p => ({ ...p, aberto: true, carregando: true, erro: '', res: null, grupo: null }))
    try {
      const { data } = await api.get<Auditoria>('/outbound/pontas-abertas', {
        params: { tenantId, from: dateFrom || undefined, to: dateTo || undefined },
      })
      setAuditoria(p => ({ ...p, carregando: false, res: data, grupo: data.grupos[0]?.tipo ?? null }))
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setAuditoria(p => ({ ...p, carregando: false, erro: err.response?.data?.title ?? 'Falha ao auditar' }))
    }
  }
  // Cutover / notas já processadas por fora: marcar enviado SEM baixar estoque.
  const [semBaixa, setSemBaixa] = useState(false)

  const [cancelId, setCancelId] = useState<string | null>(null)
  const [separando, setSeparando] = useState(false)
  const [separandoCanal, setSeparandoCanal] = useState<string | null>(null) // onda do dia por canal
  const [channel, setChannel] = useState('') // canal (originChannel exato) — '' = todos
  // Intervalo padrão: últimos 30 dias.
  const [dateFrom, setDateFrom] = useState(isoDate(new Date(Date.now() - 30 * 86400000)))
  const [dateTo, setDateTo] = useState(isoDate(new Date()))
  const [pageSize, setPageSize] = useState(100)
  const [page, setPage] = useState(1)
  // Busca server-side (todo o histórico), com debounce pra não bater a cada tecla.
  const [debouncedQ, setDebouncedQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 400)
    return () => clearTimeout(t)
  }, [q])
  const navigate = useNavigate()

  async function separarSelecionados() {
    const ids = [...selected.keys()]
    if (!ids.length || separando) return
    if (!window.confirm(`Criar um lote de separação com ${ids.length} pedido(s)? Eles entram em separação e a esteira (abastecer → imprimir → baixar) roda para o conjunto.`)) return
    setSeparando(true); setShipMsg(null)
    try {
      const { data: r } = await api.post('/separations/from-orders', { tenantId, orderIds: ids })
      setSelected(new Map())
      refetch()
      const bloq = r.bloqueados > 0
        ? `\n\n⚠️ ${r.bloqueados} pedido(s) NÃO entraram porque a NF já foi expedida: ${(r.bloqueadosNfs ?? []).join(', ')}`
        : ''
      // Um pedido fica em UMA onda ativa por vez — é o que impede a baixa dupla.
      const presos: { orderNumber: string; onda: string | null }[] = r.emOutraOnda ?? []
      const outra = presos.length
        ? `\n\n⚠️ ${presos.length} pedido(s) NÃO entraram porque já estão em outra onda ativa: ${presos.slice(0, 15).map(p => `${p.orderNumber} (${p.onda ?? '?'})`).join(', ')}${presos.length > 15 ? '…' : ''}. Tire-os de lá ou estorne a onda antes.`
        : ''
      const semEt: string[] = r.semEtiqueta ?? []
      const etq = semEt.length
        ? `\n\n⚠️ ${semEt.length} pedido(s) NÃO entraram porque estão SEM ETIQUETA: ${semEt.slice(0, 15).join(', ')}${semEt.length > 15 ? '…' : ''}. Anexe a etiqueta, sincronize o Melhor Envio, ou (admin) libere no pedido.`
        : ''
      const lib: string[] = r.liberadosForaDaOnda ?? []
      const libMsg = lib.length
        ? `\n\n⚠️ ${lib.length} pedido(s) LIBERADOS sem etiqueta ficaram de fora — eles vão em onda própria: filtre por “liberados” e separe.`
        : ''
      if (window.confirm(`Lote ${r.reference} criado com ${r.orders} pedido(s) e ${r.items} SKU(s).${bloq}${outra}${etq}${libMsg}\n\nAbrir a Separação por arquivo agora?`)) {
        navigate('/separacao-arquivo')
      }
    } catch (e: any) {
      setShipMsg(e?.response?.data?.title ?? 'Falha ao criar o lote de separação')
    } finally { setSeparando(false) }
  }

  // A onda sai do FILTRO da tela — intervalo de datas, busca, "só com etiqueta" —
  // e não do canal inteiro. Sem isso o botão varria meses de fila de uma vez e
  // produzia uma onda impossível de trabalhar num turno (1170 pedidos).
  async function separarCanal(canal: string) {
    if (separandoCanal) return
    const corpo = {
      tenantId, channel: canal,
      from: dateFrom || undefined, to: dateTo || undefined,
      search: debouncedQ || undefined, etiqueta,
    }
    setSeparandoCanal(canal); setShipMsg(null)
    try {
      // Prévia antes de criar: desfazer uma onda errada custa um estorno.
      const { data: p } = await api.post('/separations/from-board', { ...corpo, previa: true })
      if (!p.total) {
        setShipMsg(etiqueta && p.semEtiqueta > 0
          ? `Nenhum pedido do canal ${canal} passa no filtro de etiqueta "${ETIQUETA_LABEL[etiqueta]}" (${p.semEtiqueta} ficaram de fora por ele).`
          : `Nenhum pedido a separar do canal ${canal} neste filtro.`)
        return
      }
      const periodo = dateFrom || dateTo ? `\nPeríodo: ${dateFrom || '—'} até ${dateTo || '—'}` : ''
      const busca = debouncedQ ? `\nBusca: "${debouncedQ}"` : ''
      const fora = p.semEtiqueta > 0 ? `\n${p.semEtiqueta} pedido(s) ficam de fora pelo filtro de etiqueta ("${ETIQUETA_LABEL[etiqueta]}").` : ''
      if (!window.confirm(`Criar a onda com ${p.total} pedido(s) do canal ${canal}?${periodo}${busca}${fora}`)) return
      const { data: r } = await api.post('/separations/from-board', corpo)
      refetch()
      const bloq = r.bloqueados > 0 ? ` · ${r.bloqueados} já expedido(s) ignorado(s)` : ''
      const semEt: string[] = r.semEtiqueta ?? []
      const etq = semEt.length ? `\n\n⚠️ ${semEt.length} pedido(s) ficaram de fora por estarem SEM ETIQUETA: ${semEt.slice(0, 15).join(', ')}${semEt.length > 15 ? '…' : ''}.` : ''
      const outra = (r.emOutraOnda?.length ?? 0) > 0 ? `\n\n⚠️ ${r.emOutraOnda.length} pedido(s) ficaram de fora por já estarem em outra onda ativa.` : ''
      const libMsg = (r.liberadosForaDaOnda?.length ?? 0) > 0 ? `\n\n⚠️ ${r.liberadosForaDaOnda.length} pedido(s) liberados sem etiqueta ficaram de fora — vão em onda própria (filtro “liberados”).` : ''
      if (window.confirm(`Onda ${r.reference} criada: ${r.orders} pedido(s) e ${r.items} SKU(s) do canal ${canal}${bloq}.${etq}${outra}${libMsg}\n\nAbrir a Separação agora?`)) {
        navigate('/separacao-arquivo')
      }
    } catch (e: any) {
      setShipMsg(e?.response?.data?.title ?? `Falha ao separar o canal ${canal}`)
    } finally { setSeparandoCanal(null) }
  }

  function toggle(o: Order) {
    setSelected(s => { const n = new Map(s); n.has(o.id) ? n.delete(o.id) : n.set(o.id, o); return n })
  }

  // Autorização/compra em lote das pré-etiquetas (Tray cria pending → checkout no ME).
  async function comprarEtiquetas() {
    const ids = [...selected.keys()]
    if (!ids.length || comprando) return
    setComprando(true); setShipMsg(null)
    try {
      const { data: prev } = await api.post('/integrations/labels/preview', { tenantId, orderIds: ids })
      if (!prev.count) { setShipMsg('Nenhuma etiqueta pendente (pré-etiqueta) nos pedidos selecionados.'); return }
      if (!window.confirm(`Comprar ${prev.count} etiqueta(s) no Melhor Envio?\n\nTotal: R$ ${Number(prev.total).toFixed(2)} — DEBITA o saldo da carteira. Não dá pra desfazer.`)) return
      const { data: r } = await api.post('/integrations/labels/buy', { tenantId, orderIds: ids })
      setShipMsg(`${r.bought} etiqueta(s) comprada(s) · R$ ${Number(r.total ?? 0).toFixed(2)} debitado.${r.note ? ' ' + r.note : ''}`)
      refetch()
    } catch (e: any) {
      setShipMsg(e?.response?.data?.title ?? 'Falha ao comprar etiquetas (confira saldo e permissões do app no Melhor Envio)')
    } finally { setComprando(false) }
  }

  const [purging, setPurging] = useState(false)

  // Backup/exportação: o arquivo que sai do sistema para conferência, análise ou
  // cópia de segurança. Três recortes, porque são três perguntas: o pedido, o
  // que saiu dentro dele, e quem recebeu.
  const [backup, setBackup] = useState<{
    aberto: boolean; de: string; ate: string
    contagem: { pedidos: number; itens: number; clientes: number } | null
    contando: boolean; baixando: string | null; erro: string
  }>({ aberto: false, de: '', ate: '', contagem: null, contando: false, baixando: null, erro: '' })

  async function contarBackup(de: string, ate: string) {
    if (!tenantId) return
    setBackup(p => ({ ...p, contando: true, erro: '', contagem: null }))
    try {
      const { data } = await api.get('/outbound/export/contagem', {
        params: { tenantId, from: de || undefined, to: ate || undefined },
      })
      setBackup(p => ({ ...p, contando: false, contagem: data }))
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setBackup(p => ({ ...p, contando: false, erro: err.response?.data?.title ?? 'Falha ao contar os registros' }))
    }
  }

  async function baixarBackup(tipo: 'pedidos' | 'itens' | 'clientes') {
    if (!tenantId) return
    setBackup(p => ({ ...p, baixando: tipo, erro: '' }))
    try {
      const r = await api.get('/outbound/export', {
        params: { tenantId, tipo, from: backup.de || undefined, to: backup.ate || undefined },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `${tipo}-${backup.de || 'inicio'}_a_${backup.ate || 'hoje'}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 20000)
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setBackup(p => ({ ...p, erro: err.response?.data?.title ?? 'Falha ao gerar o arquivo' }))
    } finally {
      setBackup(p => ({ ...p, baixando: null }))
    }
  }

  // Etiquetas em lote: o arquivo do marketplace traz varias etiquetas e a chave
  // da NF-e esta em todas elas — o casamento e por ela, nao pelo nº do pedido
  // (que muda de formato a cada canal).
  const [lote, setLote] = useState<{
    aberto: boolean; arquivo: string | null; nome: string
    analisando: boolean; anexando: boolean
    res: { etiquetas: number; casadas: number; anexadas: number; semCasar: number; ignoradas?: number
           itens: Array<{ indice: number; orderNumber: string | null; nfNumber: string | null
                          por: string | null; anexada: boolean; motivo?: string; aviso?: string }> } | null
    erro: string
  }>({ aberto: false, arquivo: null, nome: '', analisando: false, anexando: false, res: null, erro: '' })

  // ── Etiqueta que não casou: escolher o pedido a mão ──
  // Sem isto, a página órfã só saía do arquivo se alguém recortasse o PDF por
  // fora — ninguém faz isso no meio do turno, e o pedido ia sem etiqueta. Aqui
  // dá para VER a etiqueta e apontar o pedido; o servidor recorta e anexa.
  type AchadoPedido = {
    id: string; wsCode: string | null; orderNumber: string; erpNumber: string | null
    nfNumber: string | null; customerName: string | null; originChannel: string | null
    status: string; temEtiqueta: boolean; temMelhorEnvio: boolean
  }
  const [atribuir, setAtribuir] = useState<{
    indice: number | null; termo: string; buscando: boolean
    achados: AchadoPedido[]; erro: string; salvando: string | null
    feitas: Record<number, string>
  }>({ indice: null, termo: '', buscando: false, achados: [], erro: '', salvando: null, feitas: {} })

  async function verEtiqueta(indice: number) {
    if (!lote.arquivo) return
    try {
      const r = await api.post('/outbound/labels/pagina', { fileBase64: lote.arquivo, indice }, { responseType: 'blob' })
      window.open(URL.createObjectURL(r.data as Blob), '_blank')
    } catch {
      setAtribuir(p => ({ ...p, erro: 'Não foi possível abrir esta etiqueta' }))
    }
  }

  async function buscarPedido(termo: string) {
    setAtribuir(p => ({ ...p, termo }))
    if (termo.trim().length < 2) { setAtribuir(p => ({ ...p, achados: [] })); return }
    setAtribuir(p => ({ ...p, buscando: true, erro: '' }))
    try {
      const { data } = await api.get<AchadoPedido[]>('/outbound/buscar', { params: { tenantId, q: termo.trim() } })
      setAtribuir(p => ({ ...p, achados: data, buscando: false }))
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setAtribuir(p => ({ ...p, buscando: false, erro: err.response?.data?.title ?? 'Falha ao buscar o pedido' }))
    }
  }

  async function atribuirEtiqueta(indice: number, o: AchadoPedido) {
    if (!lote.arquivo) return
    if (o.temEtiqueta && !window.confirm(`${o.wsCode ?? o.orderNumber} já tem etiqueta anexada. Substituir pela etiqueta ${indice}?`)) return
    if (o.temMelhorEnvio && !window.confirm(`${o.wsCode ?? o.orderNumber} já tem envio no Melhor Envio. Anexar outra etiqueta cria conflito: a impressa passa a ser esta, e o rastreio informado ao cliente continua o do Melhor Envio. Continuar?`)) return
    setAtribuir(p => ({ ...p, salvando: o.id, erro: '' }))
    try {
      await api.post('/outbound/labels/atribuir', { tenantId, fileBase64: lote.arquivo, indice, orderId: o.id })
      setAtribuir(p => ({
        ...p, salvando: null, indice: null, termo: '', achados: [],
        feitas: { ...p.feitas, [indice]: o.wsCode ?? o.orderNumber },
      }))
      refetch()
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setAtribuir(p => ({ ...p, salvando: null, erro: err.response?.data?.title ?? 'Falha ao anexar a etiqueta' }))
    }
  }
  const [arrastando, setArrastando] = useState(false)

  // Separação avançada: monta a onda por disponibilidade real de estoque ou por
  // produto. Nasceu do produto vendido antes do recebimento — os pedidos existem
  // mas nenhum pode ser separado, e travar a onda inteira por causa deles custa
  // o dia. O pedido entra INTEIRO ou fica INTEIRO de fora: a NF já saiu com
  // todos os itens.
  type Triado = {
    orderId: string; orderNumber: string; wsCode: string | null
    customerName: string | null; canal: string | null
    itens: { sku: string; quantity: number }[]; entra: boolean; motivo?: string
  }
  const [avancada, setAvancada] = useState<{
    aberto: boolean
    modo: 'disponivel' | 'com-produto' | 'sem-produto'
    canal: string
    productId: string
    exigirEtiqueta: boolean
    analisando: boolean; criando: boolean; erro: string
    res: { total: number; entram: Triado[]; retidos: Triado[]
           porMotivo: { motivo: string; pedidos: number }[] } | null
  }>({ aberto: false, modo: 'disponivel', canal: '', productId: '', exigirEtiqueta: true, analisando: false, criando: false, erro: '', res: null })

  const { data: produtos = [] } = useQuery<{ id: string; sku: string; name: string; active: boolean }[]>({
    queryKey: ['products', tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId } })).data,
    enabled: !!tenantId && avancada.aberto,
  })

  function corpoAvancada() {
    return {
      tenantId,
      modo: avancada.modo,
      // A onda tem que ser de UM canal — Tray e TikTok separam em ondas
      // distintas. O canal é escolhido aqui, não herdado do filtro da tela.
      canal: avancada.canal || null,
      productIds: avancada.productId ? [avancada.productId] : [],
      exigirEtiqueta: avancada.exigirEtiqueta,
      // A triagem roda sobre o MESMO recorte da tela. Sem isso ela varria a fila
      // inteira do canal, de qualquer data, e o número analisado não batia com o
      // que a tela mostrava.
      from: dateFrom || undefined, to: dateTo || undefined,
      search: debouncedQ || undefined, etiqueta,
      // Com pedidos marcados, a triagem roda só sobre eles.
      orderIds: selected.size > 0 ? [...selected.keys()] : undefined,
    }
  }

  async function analisarAvancada() {
    setAvancada(p => ({ ...p, analisando: true, erro: '', res: null }))
    try {
      const { data } = await api.post('/separations/advanced/preview', corpoAvancada())
      setAvancada(p => ({ ...p, res: data, analisando: false }))
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setAvancada(p => ({ ...p, analisando: false, erro: err?.response?.data?.title ?? 'Falha na prévia' }))
    }
  }

  async function criarAvancada() {
    setAvancada(p => ({ ...p, criando: true, erro: '' }))
    try {
      const { data: r } = await api.post('/separations/advanced', corpoAvancada())
      setAvancada(p => ({ ...p, aberto: false, criando: false, res: null }))
      setSelected(new Map())
      refetch()
      const retidos = r.retidos ? ` · ${r.retidos} pedido(s) ficaram para a próxima onda` : ''
      setShipMsg(`Onda ${r.reference} criada com ${r.orders} pedido(s) e ${r.items} SKU(s)${retidos}.`)
      if (window.confirm(`Onda ${r.reference} criada.\n\nAbrir a Separação agora?`)) navigate('/separacao-arquivo')
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setAvancada(p => ({ ...p, criando: false, erro: err?.response?.data?.title ?? 'Falha ao criar a onda' }))
    }
  }

  async function lerArquivo(f: File) {
    const b64 = await new Promise<string>((r) => {
      const fr = new FileReader()
      fr.onload = () => r(String(fr.result).split(',')[1])
      fr.readAsDataURL(f)
    })
    setLote(p => ({ ...p, arquivo: b64, nome: f.name, res: null, erro: '' }))
    await analisarLote(b64, false)
  }

  async function analisarLote(b64: string, confirmar: boolean) {
    setLote(p => ({ ...p, analisando: !confirmar, anexando: confirmar, erro: '' }))
    try {
      const { data } = await api.post('/outbound/labels/bulk', { tenantId, fileBase64: b64, confirmar })
      setLote(p => ({ ...p, res: data, analisando: false, anexando: false }))
      if (confirmar) refetch()
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setLote(p => ({ ...p, analisando: false, anexando: false,
                      erro: err?.response?.data?.title ?? 'Falha ao processar o arquivo' }))
    }
  }
  async function removerSemNf() {
    if (!window.confirm('Remover da fila os pedidos SEM nota fiscal (não despacháveis)? Eles voltam sozinhos quando a NF for emitida no ERP.')) return
    setPurging(true); setShipMsg(null)
    try {
      const { data: r } = await api.post('/outbound/purge-no-nf', { tenantId })
      setShipMsg(`${r.removed} pedido(s) sem NF removido(s) da fila.`)
      refetch()
    } catch (e: any) {
      setShipMsg(e?.response?.data?.title ?? 'Falha ao remover pedidos sem NF')
    } finally { setPurging(false) }
  }

  async function cancelar(o: Order) {
    if (!window.confirm(`Cancelar o pedido ${o.wsCode ?? o.orderNumber}? Ele sai da fila de expedição (não mexe em estoque).`)) return
    setCancelId(o.id)
    try {
      await api.patch(`/outbound/${o.id}/status`, { status: 'cancelled' })
      refetch()
    } catch (e: any) {
      alert(e?.response?.data?.title ?? 'Falha ao cancelar')
    } finally { setCancelId(null) }
  }

  async function reabrir(o: Order) {
    if (!window.confirm(`Reabrir ${o.wsCode ?? o.orderNumber} para "a despachar"?\n\nNão devolve estoque — use só se o pedido NÃO teve baixa (ex.: importado como "já enviado").`)) return
    setCancelId(o.id)
    try {
      await api.post(`/outbound/${o.id}/reopen`)
      refetch()
    } catch (e: any) {
      alert(e?.response?.data?.title ?? 'Falha ao reabrir')
    } finally { setCancelId(null) }
  }

  async function marcarEnviados() {
    const ids = [...selected.keys()]
    if (!ids.length || shipping) return
    const msg = semBaixa
      ? `Marcar ${ids.length} pedido(s) como ENVIADO em modo REGISTRO? Sai da fila e NÃO baixa estoque (para notas já expedidas por fora).`
      : `Marcar ${ids.length} pedido(s) como ENVIADO? (baixa estoque e sai da fila)`
    if (!window.confirm(msg)) return
    setShipping(true); setShipMsg(null)
    try {
      const { data: r } = await api.post('/expedition/ship-orders', { tenantId, orderIds: ids, baixa: !semBaixa })
      setShipMsg(`${r.shipped} enviado(s)${semBaixa ? ' (registro, sem baixa)' : ''}${r.already ? ` · ${r.already} já estavam` : ''}${r.blocked ? ` · ${r.blocked} bloqueado(s)` : ''}.`)
      setSelected(new Map())
      refetch()
    } catch (e: any) {
      setShipMsg(e?.response?.data?.title ?? 'Falha ao marcar enviados')
    } finally { setShipping(false) }
  }

  async function confirmarSaida() {
    const ids = [...selected.keys()]
    if (!ids.length || shipping) return
    if (!window.confirm(`Confirmar a SAÍDA de ${ids.length} pedido(s) já expedidos? (marca como Confirmado — bipado/conferido na saída)`)) return
    setShipping(true); setShipMsg(null)
    try {
      const { data: r } = await api.post('/expedition/confirm-orders', { tenantId, orderIds: ids })
      setShipMsg(`${r.confirmed} pedido(s) confirmado(s) na saída.`)
      setSelected(new Map())
      refetch()
    } catch (e: any) {
      setShipMsg(e?.response?.data?.title ?? 'Falha ao confirmar saída')
    } finally { setShipping(false) }
  }

  const [uploadingId, setUploadingId] = useState<string | null>(null)
  async function uploadLabel(orderId: string, file: File) {
    setUploadingId(orderId)
    try {
      const b64 = await new Promise<string>((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(file)
      })
      await api.post(`/outbound/${orderId}/label`, { fileBase64: b64 })
      refetch()
    } catch (e: any) {
      setDanfeErr(e?.response?.data?.title ?? 'Falha ao anexar etiqueta')
    } finally { setUploadingId(null) }
  }

  // Etiqueta em ZPL (Mercado Livre): renderiza p/ PDF 10×15 no servidor e entra na esteira.
  /** Anexa a etiqueta de um pedido — PDF ou ZPL, decidindo pelo próprio arquivo.
   *  Antes, pedido de marketplace só aceitava ZPL: quando a etiqueta vinha em PDF
   *  (TikTok, por exemplo) não havia como anexar pela tela. */
  async function uploadZpl(orderId: string, file: File) {
    setUploadingId(orderId); setDanfeErr(null)
    try {
      const ehZpl = /\.(zpl|txt)$/i.test(file.name) || (await file.slice(0, 64).text()).includes('^XA')
      if (ehZpl) {
        const zpl = await file.text()
        const { data } = await api.post(`/outbound/${orderId}/label-zpl`, { zpl })
        setShipMsg(`Etiqueta ZPL anexada (${Math.round((data?.bytes ?? 0) / 1024)} KB) — já entra na esteira 10×15.`)
      } else {
        const b64 = await new Promise<string>((res, rej) => {
          const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(file)
        })
        const { data } = await api.post(`/outbound/${orderId}/label`, { fileBase64: b64 })
        setShipMsg(`Etiqueta anexada (${Math.round((data?.bytes ?? 0) / 1024)} KB).`)
      }
      refetch()
    } catch (e: any) {
      setDanfeErr(e?.response?.data?.title ?? 'Falha ao anexar etiqueta')
    } finally { setUploadingId(null) }
  }

  async function baixarDanfe(o: Order) {
    setDanfeId(o.id); setDanfeErr(null)
    try {
      const res = await api.get(`/outbound/${o.id}/danfe`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e: any) {
      // erro chega como blob JSON quando responseType é blob
      let msg = 'Falha ao gerar a DANFE'
      try { msg = JSON.parse(await e?.response?.data?.text?.() ?? '{}').title ?? msg } catch { /* ignore */ }
      setDanfeErr(`${o.nfNumber ?? o.orderNumber}: ${msg}`)
    } finally {
      setDanfeId(null)
    }
  }

  // Troca de filtro/tamanho volta pra página 1 (senão fica numa página vazia).
  useEffect(() => { setPage(1) }, [dateFrom, dateTo, etapa, channel, pageSize, debouncedQ, etiqueta])

  const { data: board, isLoading, refetch, isFetching } = useQuery<{ orders: Order[]; total: number; channelCounts: { channel: string; count: number; separaveis: number }[] }>({
    queryKey: ['orders-board', tenantId, dateFrom, dateTo, etapa, channel, pageSize, page, debouncedQ, etiqueta],
    queryFn: async () => (await api.get('/outbound/board', { params: {
      tenantId, from: dateFrom || undefined, to: dateTo || undefined,
      page, pageSize, etapa, channel: channel || undefined, search: debouncedQ || undefined,
      etiqueta: etiqueta || undefined,
    } })).data,
    enabled: !!tenantId,
  })
  const orders = board?.orders ?? []
  const total = board?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  // Auto-carregar (job 15/15min no servidor): último horário + resultado.
  const { data: autoLoad } = useQuery<{ at: number | null; ok?: boolean; imported?: number; total?: number; failed?: number; ignored?: number; error?: string; erros?: string[] }>({
    queryKey: ['autoload-status', tenantId],
    queryFn: async () => (await api.get('/integrations/autoload-status', { params: { tenantId } })).data,
    enabled: !!tenantId,
    refetchInterval: 60000,
  })
  const agoLabel = (ts: number) => {
    const min = Math.max(0, Math.round((Date.now() - ts) / 60000))
    const hhmm = new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    if (min < 1) return `agora (${hhmm})`
    if (min < 60) return `há ${min} min (${hhmm})`
    return `${hhmm}`
  }

  // Corte/expedição do canal filtrado (config informativa) — ondas por canal.
  const { data: channelSched = {} } = useQuery<Record<string, { cutoff: string; maxExpedition: string }>>({
    queryKey: ['channel-schedule'],
    queryFn: async () => (await api.get('/integrations/channel-schedule')).data,
  })
  const schedFor = channel
    ? (() => {
        const label = channel.toLowerCase()
        const e = Object.entries(channelSched).find(([n]) => n.toLowerCase().includes(label) || label.includes(n.toLowerCase()))
        return e ? { canal: e[0], ...e[1] } : null
      })()
    : null

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  // Etapa/canal/data/busca filtram no SERVIDOR. Aqui só o "só pendentes" refina a página.
  const channelCounts = board?.channelCounts ?? []
  const filtered = soPendentes ? orders.filter(o => !o.pronto) : orders

  // Seleção inteligente: selecionados ficam fixos no topo (mesmo mudando de página);
  // o resto é a página atual.
  const selectedList = [...selected.values()]
  const rest = filtered.filter(o => !selected.has(o.id))

  const renderRow = (o: Order) => {
    const li = labelInfo(o.label)
    return (
      <tr key={o.id} className={`hover:bg-gray-50 ${selected.has(o.id) ? 'bg-blue-50' : ''}`}>
        {canEdit && (
          <td className="px-3 py-3">
            <input type="checkbox" className="h-4 w-4" checked={selected.has(o.id)} onChange={() => toggle(o)} />
          </td>
        )}
        <td className="px-4 py-3 font-mono font-semibold text-gray-900">{o.wsCode ?? <span className="text-gray-300">—</span>}</td>
        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
          {(o.emissionDate ?? o.createdAt)
            ? new Date(o.emissionDate ?? o.createdAt).toLocaleDateString('pt-BR')
            : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-4 py-3 font-medium text-gray-800">
          <button className="text-blue-600 hover:underline" title="Abrir o pedido"
            onClick={() => navigate(`/outbound/${o.id}`)}>
            {o.erpNumber ?? o.orderNumber}
          </button>
          {o.erpNumber && o.orderNumber && o.erpNumber !== o.orderNumber &&
            <span className="ml-1 text-xs text-gray-400">(nº loja {o.orderNumber})</span>}
        </td>
        <td className="px-4 py-3 font-mono text-gray-700">
          {o.nfNumber
            ? <span title={o.nfKey ?? undefined}>{o.nfNumber}{nfSerie(o) ? <span className="text-gray-400">·{nfSerie(o)}</span> : ''}</span>
            : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-4 py-3 text-gray-600">{o.customerName ?? '—'}</td>
        <td className="px-4 py-3"><ChannelIcon name={o.originChannel} orderNumber={o.orderNumber} nfNumber={o.nfNumber} /></td>
        <td className="px-4 py-3 text-gray-600">{o.carrier ?? '—'}</td>
        <td className="px-4 py-3 whitespace-nowrap">
          {!o.label && o.marketplace ? (
            <span className="inline-flex items-center gap-2">
              {o.hasLabelDoc
                ? <Badge variant="success">Etiqueta anexada</Badge>
                : <span className="rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700" title="Etiqueta impressa no marketplace/Bling (não passa pelo WsFull)">Etiqueta externa</span>}
              {canEdit && (
                <label className="cursor-pointer text-xs text-blue-600 hover:underline" title="Subir a etiqueta do marketplace — PDF ou ZPL (o ZPL vira PDF 10×15)">
                  {uploadingId === o.id ? 'enviando…' : o.hasLabelDoc ? 'trocar' : 'anexar'}
                  <input type="file" accept=".pdf,.zpl,.txt" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadZpl(o.id, f); e.currentTarget.value = '' }} />
                </label>
              )}
            </span>
          ) : temConflito(o) ? (
            <span className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-amber-700"
              title={`Etiqueta anexada E envio no Melhor Envio (${li.text}${o.label?.trackingCode ? `, rastreio ${o.label.trackingCode}` : ''}). A impressa é a anexada — confira antes de despachar.`}>
              ✓ Anexo <span className="text-gray-400">(+ ME sem uso)</span>
            </span>
          ) : (
            <>
              <Badge variant={li.variant}>{li.text}</Badge>
              {canEdit && !o.label && !o.marketplace && (
                <label className="ml-2 cursor-pointer text-xs text-blue-600 hover:underline">
                  {uploadingId === o.id ? 'enviando…' : 'anexar'}
                  <input type="file" accept="application/pdf" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadLabel(o.id, f); e.currentTarget.value = '' }} />
                </label>
              )}
            </>
          )}
        </td>
        <td className="px-4 py-3">
          <span className="inline-flex flex-wrap items-center gap-1">
            <Badge variant={orderStatusVariant[o.status] ?? 'default'}>{orderStatusLabel[o.status] ?? o.status}</Badge>
            <OndaTag o={o} navigate={navigate} />
          </span>
          {o.confirmedAt && <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700" title={`Confirmado na saída em ${new Date(o.confirmedAt).toLocaleString('pt-BR')}`}><Check className="h-3 w-3" />conf.</span>}
        </td>
        <td className="px-4 py-3 font-mono text-xs">
          {o.label?.trackingCode
            ? <a href={trackingUrl(o.carrier, o.label.trackingCode)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{o.label.trackingCode}</a>
            : <span className="text-gray-400">—</span>}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1">
            {o.nfNumber ? (
              <Button size="sm" variant="secondary" loading={danfeId === o.id} onClick={() => baixarDanfe(o)}>
                <FileText className="h-4 w-4" /> DANFE
              </Button>
            ) : <span className="text-gray-300">—</span>}
            {canEdit && o.status !== 'shipped' && o.status !== 'cancelled' && (
              <Button size="sm" variant="ghost" title="Cancelar pedido"
                loading={cancelId === o.id} onClick={() => cancelar(o)}>
                <Ban className="h-4 w-4 text-red-500" />
              </Button>
            )}
            {canEdit && o.status === 'shipped' && (
              <button title="Reabrir para a despachar (não devolve estoque)"
                disabled={cancelId === o.id} onClick={() => reabrir(o)}
                className="text-xs text-blue-600 hover:underline disabled:opacity-40">
                reabrir
              </button>
            )}
          </div>
        </td>
      </tr>
    )
  }

  // Cartão para celular (o table de 11 colunas não cabe no telefone)
  const renderCard = (o: Order) => {
    const li = labelInfo(o.label)
    return (
      <div key={o.id} className={`flex gap-3 px-4 py-3 ${selected.has(o.id) ? 'bg-blue-50' : ''}`}>
        {canEdit && <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={selected.has(o.id)} onChange={() => toggle(o)} />}
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono font-semibold text-gray-900">{o.wsCode ?? '—'}</span>
            <ChannelIcon name={o.originChannel} orderNumber={o.orderNumber} nfNumber={o.nfNumber} />
            <Badge variant={orderStatusVariant[o.status] ?? 'default'}>{orderStatusLabel[o.status] ?? o.status}</Badge>
          <OndaTag o={o} navigate={navigate} />
          </div>
          <p className="truncate text-sm text-gray-700">{o.customerName ?? '—'}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
            <span>ERP <b className="text-gray-700">{o.erpNumber ?? o.orderNumber}</b></span>
            <span>NF <b className="text-gray-700">{o.nfNumber ? `${o.nfNumber}${nfSerie(o) ? '·' + nfSerie(o) : ''}` : '—'}</b></span>
            {o.carrier && <span className="truncate">{o.carrier}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            {!o.label && o.marketplace ? (
              <>
                {o.hasLabelDoc
                  ? <Badge variant="success">Etiqueta anexada</Badge>
                  : <span className="rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700" title="Etiqueta impressa no marketplace/Bling (não passa pelo WsFull)">Etiqueta externa</span>}
                {canEdit && (
                  <label className="cursor-pointer text-xs text-blue-600 hover:underline" title="Subir a etiqueta do marketplace — PDF ou ZPL (o ZPL vira PDF 10×15)">
                    {uploadingId === o.id ? 'enviando…' : o.hasLabelDoc ? 'trocar' : 'anexar'}
                    <input type="file" accept=".pdf,.zpl,.txt" className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) uploadZpl(o.id, f); e.currentTarget.value = '' }} />
                  </label>
                )}
              </>
            ) : temConflito(o) ? (
              <span className="font-medium text-amber-700"
                title={`Etiqueta anexada E envio no Melhor Envio (${li.text}). A impressa é a anexada — confira antes de despachar.`}>
                ✓ Anexo <span className="text-gray-400">(+ ME sem uso)</span>
              </span>
            ) : (
              <Badge variant={li.variant}>{li.text}</Badge>
            )}
            {canEdit && !o.label && !o.marketplace && (
              <label className="cursor-pointer text-xs text-blue-600 hover:underline">
                {uploadingId === o.id ? 'enviando…' : 'anexar etiqueta'}
                <input type="file" accept="application/pdf" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadLabel(o.id, f); e.currentTarget.value = '' }} />
              </label>
            )}
            {o.label?.trackingCode && (
              <a href={trackingUrl(o.carrier, o.label.trackingCode)} target="_blank" rel="noopener noreferrer"
                className="font-mono text-xs text-blue-600 hover:underline">{o.label.trackingCode}</a>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            {o.nfNumber && (
              <Button size="sm" variant="secondary" loading={danfeId === o.id} onClick={() => baixarDanfe(o)}>
                <FileText className="h-4 w-4" /> DANFE
              </Button>
            )}
            {canEdit && o.status !== 'shipped' && o.status !== 'cancelled' && (
              <Button size="sm" variant="ghost" loading={cancelId === o.id} onClick={() => cancelar(o)}>
                <Ban className="h-4 w-4 text-red-500" /> Cancelar
              </Button>
            )}
            {canEdit && o.status === 'shipped' && (
              <button disabled={cancelId === o.id} onClick={() => reabrir(o)}
                className="text-xs text-blue-600 hover:underline disabled:opacity-40">reabrir</button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Pedidos a despachar</h2>
          <p className="text-sm text-gray-500">Podem ser enviados e ainda não foram · por nota fiscal</p>
          {autoLoad?.at && (
            <p className="mt-0.5 text-xs text-gray-400">
              <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${autoLoad.ok === false ? 'bg-red-400' : 'bg-emerald-400'}`} />
              Auto-carregar {agoLabel(autoLoad.at)}
              {autoLoad.ok === false
                ? ` · erro: ${autoLoad.error ?? 'falha'}`
                : ` · ${autoLoad.imported ?? 0} importado(s) de ${autoLoad.total ?? 0}${autoLoad.ignored ? ` · ${autoLoad.ignored} ignorada(s) pelo blocklist` : ''}${autoLoad.failed ? ` · ${autoLoad.failed} falha(s)` : ''}`}
            </p>
          )}
          {/* "17 falha(s)" sem motivo não ajuda ninguém — o detalhe fica a um clique. */}
          {!!autoLoad?.failed && !!autoLoad.erros?.length && (
            <details className="mt-0.5 text-xs">
              <summary className="cursor-pointer text-amber-600">ver por que {autoLoad.failed} falharam</summary>
              <ul className="mt-1 space-y-0.5 text-gray-500">
                {autoLoad.erros.map((e, i) => <li key={i} className="break-all">· {e}</li>)}
              </ul>
            </details>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && selected.size > 0 && (
            <Button size="sm" loading={separando} onClick={separarSelecionados}>
              <Layers className="h-4 w-4" /> Separar ({selected.size})
            </Button>
          )}
          {canEdit && selected.size > 0 && !isAgent && (
            <Button size="sm" variant="secondary" loading={comprando} onClick={comprarEtiquetas}>
              <Tag className="h-4 w-4" /> Comprar etiquetas ({selected.size})
            </Button>
          )}
          {canEdit && selected.size > 0 && !isAgent && (
            <Button size="sm" variant="secondary" loading={shipping} onClick={marcarEnviados}>
              <Truck className="h-4 w-4" /> Marcar enviados ({selected.size})
            </Button>
          )}
          {canEdit && selected.size > 0 && (
            <Button size="sm" variant="secondary" loading={shipping} onClick={confirmarSaida} title="Marca os já expedidos como Confirmado (bipado/conferido na saída) — admin">
              <Check className="h-4 w-4" /> Confirmar saída ({selected.size})
            </Button>
          )}
          {canEdit && selected.size > 0 && !isAgent && (
            <label className="flex items-center gap-1 text-xs text-gray-500" title="Só marca como enviado e tira da fila — não baixa estoque. Para notas já expedidas por fora / cutover.">
              <input type="checkbox" className="h-3.5 w-3.5" checked={semBaixa} onChange={e => setSemBaixa(e.target.checked)} />
              registro (sem baixa)
            </label>
          )}
          {canEdit && !isAgent && (
            <Button variant="secondary" size="sm"
              title="Baixa um CSV com os pedidos, os itens e os destinatários deste cliente"
              onClick={() => { setBackup(p => ({ ...p, aberto: true, erro: '' })); contarBackup(backup.de, backup.ate) }}>
              <Database className="h-4 w-4" /> Backup / exportar
            </Button>
          )}
          {canEdit && (
            <Button variant="secondary" size="sm" onClick={auditar} loading={auditoria.carregando}
              title="Procura pedidos cujo estado se contradiz — expedido sem baixa, expedido antes da etiqueta, parado em separação">
              <ShieldAlert className="h-4 w-4" /> Pontas abertas
            </Button>
          )}
          {canEdit && (
            <Button variant="secondary" size="sm"
              onClick={() => { setAvancada(p => ({ ...p, aberto: true, canal: p.canal || channel, res: null, erro: '' })); }}
              title="Monta a onda por disponibilidade de estoque ou por produto — o que não dá para atender fica para a próxima">
              <Filter className="h-4 w-4" /> Separação avançada
            </Button>
          )}
          {canEdit && (
            <Button variant="secondary" size="sm"
              onClick={() => setLote({ aberto: true, arquivo: null, nome: '', analisando: false, anexando: false, res: null, erro: '' })}
              title="Sobe o arquivo de etiquetas do marketplace (PDF ou ZPL) e casa cada uma com o pedido pela chave da NF">
              <Upload className="h-4 w-4" /> Etiquetas em lote
            </Button>
          )}
          <Button variant="secondary" size="sm" loading={isFetching} onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" /> Atualizar
          </Button>
          {canEdit && (
            <button type="button" disabled={purging} onClick={removerSemNf}
              className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50"
              title="Remove da fila os pedidos importados sem nota fiscal (não despacháveis)">
              {purging ? 'removendo…' : 'limpar sem NF'}
            </button>
          )}
        </div>
      </div>

      {shipMsg && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">{shipMsg}</div>
      )}

      {/* Pontas abertas: pedidos cujo estado se contradiz. O ponto não é acusar —
          é transformar "será que tem mais?" numa lista finita e navegável. */}
      <Modal title="Backup / exportar pedidos" open={backup.aberto} size="lg"
        onClose={() => setBackup(p => ({ ...p, aberto: false }))}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Três arquivos do mesmo período, em CSV (abre no Excel). O arquivo sai com os dados
            do destinatário — nome, documento e endereço —, então trate-o como dado pessoal.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-gray-600">
              De
              <input type="date" value={backup.de} className="ml-2 rounded-lg border border-gray-200 px-2 py-1 text-sm"
                onChange={e => { const v = e.target.value; setBackup(p => ({ ...p, de: v })); contarBackup(v, backup.ate) }} />
            </label>
            <label className="text-sm text-gray-600">
              até
              <input type="date" value={backup.ate} className="ml-2 rounded-lg border border-gray-200 px-2 py-1 text-sm"
                onChange={e => { const v = e.target.value; setBackup(p => ({ ...p, ate: v })); contarBackup(backup.de, v) }} />
            </label>
            <span className="text-xs text-gray-400">
              em branco = tudo, desde o começo · a data é a da emissão da NF (ou a de criação, quando não há nota)
            </span>
          </div>

          {backup.erro && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{backup.erro}</p>}

          <div className="grid gap-3 sm:grid-cols-3">
            {([
              ['pedidos', 'Pedidos', 'Uma linha por pedido: nº, canal, NF, destinatário e endereço, transportadora e rastreio, onda, baixa de estoque, produtos resumidos.'],
              ['itens', 'Itens', 'Uma linha por produto de cada pedido — o que de fato saiu, para somar por SKU.'],
              ['clientes', 'Destinatários', 'Uma linha por pessoa que recebeu: pedidos, unidades, valor em NF, cidade/UF, primeiro e último pedido.'],
            ] as const).map(([tipo, titulo, desc]) => (
              <div key={tipo} className="flex flex-col justify-between gap-2 rounded-lg border border-gray-200 p-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{titulo}</p>
                  <p className="mt-0.5 text-xs text-gray-500">{desc}</p>
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-400">
                    {backup.contando ? 'contando…' : backup.contagem
                      ? `${backup.contagem[tipo].toLocaleString('pt-BR')} linha(s)`
                      : '—'}
                  </p>
                  <Button size="sm" className="w-full" loading={backup.baixando === tipo}
                    disabled={!!backup.baixando || backup.contagem?.[tipo] === 0}
                    onClick={() => baixarBackup(tipo)}>
                    <Database className="h-4 w-4" /> Baixar CSV
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400">
            Arquivo grande demora: ele sai em partes, não trave a aba. Para um backup mensal,
            escolha o mês no período acima.
          </p>
        </div>
      </Modal>

      <Modal title="Pontas abertas" open={auditoria.aberto} size="lg"
        onClose={() => setAuditoria(p => ({ ...p, aberto: false }))}>
        {auditoria.carregando ? (
          <p className="py-8 text-center text-sm text-gray-400">Conferindo os pedidos do período…</p>
        ) : auditoria.erro ? (
          <p className="text-sm text-red-600">{auditoria.erro}</p>
        ) : auditoria.res ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              <b>{auditoria.res.pedidosComPonta}</b> pedido(s) com alguma contradição, de{' '}
              <b>{auditoria.res.analisados}</b> analisados no período {dateFrom || '—'} a {dateTo || '—'}.
              {' '}Um mesmo pedido pode aparecer em mais de um grupo.
            </p>

            {auditoria.res.grupos.length === 0 ? (
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                Nada fora do lugar neste período.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {auditoria.res.grupos.map(g => (
                    <button key={g.tipo} onClick={() => setAuditoria(p => ({ ...p, grupo: g.tipo }))}
                      className={`rounded-lg border px-3 py-2 text-left text-xs ${auditoria.grupo === g.tipo ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <span className="block font-semibold text-gray-900">{g.total}</span>
                      <span className="block text-gray-600">{g.titulo}</span>
                    </button>
                  ))}
                </div>

                {auditoria.res.grupos.filter(g => g.tipo === auditoria.grupo).map(g => (
                  <div key={g.tipo} className="space-y-2">
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{g.explica}</p>
                    <div className="max-h-80 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-white text-gray-500">
                          <tr className="border-b border-gray-200">
                            <th className="px-2 py-1 text-left">Pedido</th>
                            <th className="px-2 py-1 text-left">NF</th>
                            <th className="px-2 py-1 text-left">Canal</th>
                            <th className="px-2 py-1 text-left">Status</th>
                            <th className="px-2 py-1 text-left">O que não fecha</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.pedidos.map(o => (
                            <tr key={o.id} className="border-b border-gray-100">
                              <td className="px-2 py-1">
                                <button className="font-mono text-blue-600 hover:underline"
                                  onClick={() => navigate(`/outbound/${o.id}`)}>{o.pedido}</button>
                              </td>
                              <td className="px-2 py-1">{o.nf ?? '—'}</td>
                              <td className="px-2 py-1">{o.canal}</td>
                              <td className="px-2 py-1">{orderStatusLabel[o.status] ?? o.status}</td>
                              <td className="px-2 py-1 text-gray-600">{o.detalhe}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {g.total > g.pedidos.length && (
                      <p className="text-xs text-gray-400">Mostrando {g.pedidos.length} de {g.total} — reduza o período para ver o resto.</p>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        ) : null}
      </Modal>

      <Modal title="Separação avançada" open={avancada.aberto} size="lg"
        onClose={() => setAvancada(p => ({ ...p, aberto: false }))}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Monta a onda escolhendo <strong>quais pedidos entram</strong>. O que não entrar continua
            na fila, esperando a próxima separação. Pedido entra inteiro ou não entra — a NF já saiu
            com todos os itens.
          </p>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Canal *</label>
            <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={avancada.canal}
              onChange={e => setAvancada(p => ({ ...p, canal: e.target.value, res: null }))}>
              <option value="">Selecione o canal...</option>
              {channelCounts.map(c => (
                <option key={c.channel} value={c.channel}>{c.channel} ({c.separaveis} a separar)</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-400">
              A onda é sempre de um canal só — Tray e TikTok separam em ondas distintas.
            </p>
          </div>

          <div className="space-y-2">
            {([
              ['disponivel', 'O que dá para atender hoje', 'O sistema confere o saldo (fracionamento + armazém) de cada item. Pedido que depende de produto em falta fica de fora, com o motivo.'],
              ['com-produto', 'Só pedidos COM um produto', 'Junta numa onda só os pedidos que contêm o produto escolhido.'],
              ['sem-produto', 'Excluir pedidos com um produto', 'Deixa de fora os pedidos que contêm o produto escolhido — o caso do item que ainda não foi recebido.'],
            ] as const).map(([valor, titulo, desc]) => (
              <label key={valor} className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${avancada.modo === valor ? 'border-blue-500 bg-blue-50/40' : 'border-gray-200'}`}>
                <input type="radio" className="mt-1" checked={avancada.modo === valor}
                  onChange={() => setAvancada(p => ({ ...p, modo: valor, res: null }))} />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{titulo}</span>
                  <span className="block text-xs text-gray-500">{desc}</span>
                </span>
              </label>
            ))}
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3">
            <input type="checkbox" className="mt-1" checked={avancada.exigirEtiqueta}
              onChange={e => setAvancada(p => ({ ...p, exigirEtiqueta: e.target.checked, res: null }))} />
            <span>
              <span className="block text-sm font-medium text-gray-900">Só pedidos com etiqueta pronta</span>
              <span className="block text-xs text-gray-500">
                Etiqueta anexada no WsFull ou envio ativo no Melhor Envio. Pedido de marketplace
                cuja etiqueta ainda não foi subida fica de fora — sem ela a esteira não imprime.
              </span>
            </span>
          </label>

          {avancada.modo !== 'disponivel' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Produto</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={avancada.productId}
                onChange={e => setAvancada(p => ({ ...p, productId: e.target.value, res: null }))}>
                <option value="">Selecione...</option>
                {produtos.filter(p => p.active).map(p => (
                  <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
                ))}
              </select>
            </div>
          )}

          <p className="text-xs text-gray-400">
            {selected.size > 0
              ? `Triagem sobre os ${selected.size} pedido(s) marcados${avancada.canal ? `, limitada ao canal ${avancada.canal}` : ''}.`
              : avancada.canal
                ? `Triagem sobre os pedidos "a separar" do canal ${avancada.canal} no filtro da tela — ${dateFrom || '—'} a ${dateTo || '—'}${etiqueta ? `, ${ETIQUETA_LABEL[etiqueta]}` : ''}${debouncedQ ? `, busca "${debouncedQ}"` : ''} — em todas as páginas.`
                : 'Escolha o canal para começar.'}
          </p>

          {avancada.erro && <p className="text-sm text-red-600">{avancada.erro}</p>}

          {avancada.res && (
            <div className="space-y-3">
              <div className="flex gap-4 text-sm">
                <span className="font-medium text-green-700">{avancada.res.entram.length} entram</span>
                <span className="font-medium text-amber-600">{avancada.res.retidos.length} ficam para a próxima</span>
                <span className="text-gray-400">de {avancada.res.total} analisados</span>
              </div>

              {avancada.res.porMotivo.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="mb-1 text-xs font-medium text-amber-800">Por que ficaram de fora</p>
                  <ul className="space-y-0.5 text-xs text-amber-700">
                    {avancada.res.porMotivo.map((m, i) => (
                      <li key={i}>{m.pedidos} pedido(s) · {m.motivo}</li>
                    ))}
                  </ul>
                </div>
              )}

              {avancada.res.retidos.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-500">ver os pedidos retidos</summary>
                  <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-100">
                    {avancada.res.retidos.map(r => (
                      <div key={r.orderId} className="flex gap-2 border-b border-gray-100 px-3 py-1.5 last:border-0">
                        <span className="font-mono text-gray-700">{r.wsCode ?? r.orderNumber}</span>
                        <span className="flex-1 truncate text-gray-500">{r.customerName ?? '—'}</span>
                        <span className="text-amber-700">{r.motivo}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => setAvancada(p => ({ ...p, aberto: false }))}>Fechar</Button>
            <Button variant="secondary" loading={avancada.analisando}
              disabled={!avancada.canal || (avancada.modo !== 'disponivel' && !avancada.productId)}
              onClick={analisarAvancada}>
              Analisar
            </Button>
            {avancada.res && avancada.res.entram.length > 0 && (
              <Button loading={avancada.criando} onClick={criarAvancada}>
                Criar onda com {avancada.res.entram.length}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      <Modal title="Etiquetas em lote" open={lote.aberto} size="lg"
        onClose={() => setLote(p => ({ ...p, aberto: false }))}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Suba o arquivo de etiquetas do marketplace — <strong>PDF</strong> (uma por página) ou
            <strong> ZPL</strong>. Cada etiqueta é casada com o pedido pela <strong>chave da NF-e</strong>,
            que aparece em todas elas. Nada é anexado até você confirmar.
          </p>

          <label
            onDragOver={e => { e.preventDefault(); setArrastando(true) }}
            onDragLeave={() => setArrastando(false)}
            onDrop={e => {
              e.preventDefault(); setArrastando(false)
              const f = e.dataTransfer.files?.[0]
              if (f && !lote.analisando) lerArquivo(f)
            }}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-8 text-sm transition-colors ${
              arrastando ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-blue-300 hover:text-blue-600'
            }`}>
            <Upload className="mb-2 h-6 w-6" />
            <span className="font-medium">
              {lote.analisando ? 'analisando…' : lote.nome || 'Arraste o arquivo aqui'}
            </span>
            {!lote.analisando && !lote.nome && (
              <span className="mt-1 text-xs text-gray-400">ou clique para escolher · .pdf, .zpl, .txt</span>
            )}
            <input type="file" accept=".pdf,.zpl,.txt" className="hidden" disabled={lote.analisando}
              onChange={e => { const f = e.target.files?.[0]; if (f) lerArquivo(f); e.currentTarget.value = '' }} />
          </label>

          {lote.erro && <p className="text-sm text-red-600">{lote.erro}</p>}

          {lote.res && (
            <>
              <div className="flex gap-4 text-sm">
                <span className="text-gray-500">{lote.res.etiquetas} etiqueta(s)</span>
                <span className="font-medium text-green-700">{lote.res.casadas} casadas</span>
                {lote.res.semCasar > 0 && <span className="font-medium text-amber-600">{lote.res.semCasar} sem casar</span>}
                {!!lote.res.ignoradas && <span className="text-gray-400">{lote.res.ignoradas} lista(s) ignorada(s)</span>}
                {lote.res.anexadas > 0 && <span className="font-medium text-blue-700">{lote.res.anexadas} anexadas</span>}
              </div>

              <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Pedido</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">NF</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Situação</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lote.res.itens.map(i => (
                      <tr key={i.indice} className={i.motivo ? 'bg-red-50' : i.orderNumber ? '' : 'bg-amber-50'}>
                        <td className="px-3 py-2 text-gray-400">{i.indice}</td>
                        <td className="px-3 py-2 font-medium text-gray-800">{i.orderNumber ?? '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">{i.nfNumber ?? '—'}</td>
                        <td className="px-3 py-2 text-xs">
                          {/* O motivo vem primeiro: quando o pedido casa mas o anexo
                              falha, esconder a mensagem faz parecer que deu certo. */}
                          {i.motivo
                            ? <span className="text-red-600">{i.motivo}</span>
                            : (
                              <>
                                {i.anexada
                                  ? <span className="text-green-700">✓ anexada</span>
                                  : i.orderNumber
                                    ? <span className="text-gray-500">casada por {i.por}</span>
                                    : <span className="text-amber-700">não casou</span>}
                                {/* Aviso não impede: a etiqueta foi anexada, mas
                                    algo merece um olhar. */}
                                {i.aviso && <span className="block text-amber-700">{i.aviso}</span>}
                              </>
                            )}
                          {atribuir.feitas[i.indice] && (
                            <span className="block text-green-700">✓ anexada a mão em {atribuir.feitas[i.indice]}</span>
                          )}
                        </td>
                        {/* Coluna de saída manual: só para quem não casou. */}
                        <td className="px-3 py-2 text-right">
                          {!i.orderNumber && !atribuir.feitas[i.indice] && !i.motivo?.startsWith('lista de conferência') && (
                            <div className="flex justify-end gap-1">
                              <button className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                                onClick={() => verEtiqueta(i.indice)}>ver</button>
                              <button className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100"
                                onClick={() => setAtribuir(p => ({ ...p, indice: p.indice === i.indice ? null : i.indice, termo: '', achados: [], erro: '' }))}>
                                {atribuir.indice === i.indice ? 'fechar' : 'escolher pedido'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {/* A busca abre na linha da etiqueta: quem está com ela na
                        mão procura pelo que consegue ler — nº do pedido, NF ou
                        o nome do destinatário. */}
                    {lote.res.itens.filter(i => i.indice === atribuir.indice).map(i => (
                      <tr key={`busca-${i.indice}`}>
                        <td colSpan={5} className="bg-blue-50/50 px-3 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-gray-600">Etiqueta {i.indice} →</span>
                            <input autoFocus value={atribuir.termo} onChange={e => buscarPedido(e.target.value)}
                              placeholder="nº do pedido, WS, NF, chave ou nome do destinatário"
                              className="min-w-64 flex-1 rounded-lg border border-gray-200 px-2 py-1 text-sm" />
                            {atribuir.buscando && <span className="text-xs text-gray-400">buscando…</span>}
                          </div>
                          {atribuir.erro && <p className="mt-2 text-xs text-red-600">{atribuir.erro}</p>}
                          {!!atribuir.achados.length && (
                            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                              {atribuir.achados.map(o => (
                                <button key={o.id} disabled={!!atribuir.salvando}
                                  onClick={() => atribuirEtiqueta(i.indice, o)}
                                  className="flex w-full items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-left text-xs last:border-0 hover:bg-blue-50 disabled:opacity-50">
                                  <span>
                                    <span className="font-medium text-gray-800">{o.wsCode ?? o.orderNumber}</span>
                                    <span className="text-gray-500"> · {o.customerName ?? 'sem destinatário'}</span>
                                    {o.nfNumber && <span className="text-gray-400"> · NF {o.nfNumber}</span>}
                                    {o.originChannel && <span className="text-gray-400"> · {o.originChannel}</span>}
                                  </span>
                                  <span className="shrink-0 text-gray-400">
                                    {o.temEtiqueta && <span className="mr-1 text-amber-600">já tem etiqueta</span>}
                                    {o.temMelhorEnvio && <span className="mr-1 text-amber-600">tem ME</span>}
                                    {atribuir.salvando === o.id ? 'anexando…' : 'anexar aqui'}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                          {!atribuir.achados.length && atribuir.termo.trim().length >= 2 && !atribuir.buscando && (
                            <p className="mt-2 text-xs text-gray-500">Nenhum pedido deste cliente bate com “{atribuir.termo}”.</p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {lote.res.semCasar > 0 && (
                <p className="text-xs text-amber-700">
                  As que não casaram não precisam sair do arquivo: clique em <b>ver</b> para conferir a etiqueta
                  e em <b>escolher pedido</b> para anexá-la a mão. Chave não encontrada costuma ser NF ainda não sincronizada.
                </p>
              )}
            </>
          )}

          {/* Sem um "pronto" explícito, a tela parecia travada: o botão de ação
              sumia e sobrava um Fechar apagado. */}
          {!!lote.res?.anexadas && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              <strong>Pronto.</strong> {lote.res.anexadas} etiqueta(s) anexada(s) aos pedidos.
              Eles já aparecem como "Etiqueta anexada" na lista.
              {lote.res.semCasar > 0 && ` ${lote.res.semCasar} não casou e ficou de fora.`}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            {lote.res?.anexadas ? (
              <Button onClick={() => { setShipMsg(`${lote.res!.anexadas} etiqueta(s) anexada(s) por lote.`); setLote(p => ({ ...p, aberto: false })) }}>
                Concluir
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setLote(p => ({ ...p, aberto: false }))}>Fechar</Button>
                {lote.res && lote.res.casadas > 0 && (
                  <Button loading={lote.anexando} onClick={() => analisarLote(lote.arquivo!, true)}>
                    Anexar {lote.res.casadas} etiqueta(s)
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Dois cards na mesma linha: pedidos por canal + resumo do filtro */}
      <div className="grid gap-3 lg:grid-cols-2">
        {/* Pedidos por canal (respeita a etapa filtrada) — clique = filtra a onda do canal */}
        <Card>
          <CardContent className="py-4">
            <p className="mb-2 text-xs font-medium text-gray-500">Pedidos por canal</p>
            {channelCounts.length === 0 ? (
              <p className="text-sm text-gray-400">—</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {channelCounts.map(c => (
                  <button key={c.channel} onClick={() => setChannel(c.channel === channel ? '' : c.channel)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${channel === c.channel ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                    <ChannelIcon name={c.channel} />
                    <span className="text-xs text-gray-500">{c.channel}</span>
                    <span className="text-lg font-bold text-gray-900">{c.count}</span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Resumo do filtro atual (todas as páginas) */}
        <Card>
          <CardContent className="py-4">
            <p className="mb-2 text-xs font-medium text-gray-500">Resumo</p>
            <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
              <div><p className="text-xs text-gray-500">Pedidos no filtro</p><p className="text-2xl font-bold text-gray-900">{total}</p></div>
              <div><p className="text-xs text-gray-500">Página</p><p className="text-2xl font-bold text-gray-900">{page} <span className="text-base font-normal text-gray-400">de {pageCount}</span></p></div>
              <div><p className="text-xs text-gray-500">Nesta página</p><p className="text-2xl font-bold text-gray-900">{orders.length}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Onda a partir do filtro da tela (todas as páginas, não só as 100 visíveis) */}
      {canEdit && channelCounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2.5">
          <span className="text-xs font-semibold text-blue-900">
            Separar tudo do filtro ({dateFrom || '—'} a {dateTo || '—'}{etiqueta ? `, ${ETIQUETA_LABEL[etiqueta]}` : ''}) — canal:
          </span>
          {/* O número aqui é o que ENTRA na onda (só os "a separar"), não o total
              da etapa: pedido já em separação ou embalado não volta para uma onda
              nova, e o botão prometia mais do que criava. */}
          {channelCounts.map(c => (
            <Button key={c.channel} size="sm" variant="secondary" loading={separandoCanal === c.channel}
              disabled={!!separandoCanal || c.separaveis === 0} onClick={() => separarCanal(c.channel)}>
              <Layers className="h-4 w-4" /> {c.channel} ({c.separaveis})
            </Button>
          ))}
        </div>
      )}

      {/* Etapa de expedição (filtro principal) + só pendentes */}
      <div className="flex flex-wrap items-center gap-2">
        {([['a-enviar', 'A enviar'], ['a-separar', 'A separar'], ['em-separacao', 'Em separação'], ['expedidos', 'Expedidos'], ['confirmados', 'Confirmados'], ['sem-nf', 'Sem NF'], ['todos', 'Todos']] as const).map(([id, label]) => (
          <button key={id} onClick={() => setEtapa(id)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${etapa === id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {label}
          </button>
        ))}
        <label className="ml-2 flex items-center gap-1 text-xs text-gray-500" title="Só os pedidos que ainda faltam etiqueta/documento (marketplace conta como pronto).">
          <input type="checkbox" className="h-3.5 w-3.5" checked={soPendentes} onChange={e => setSoPendentes(e.target.checked)} />
          só pendentes
        </label>
        <label className="flex items-center gap-1 text-xs text-gray-500" title="Filtra pela etiqueta. 'Conflito' = tem anexo E envio no Melhor Envio: a impressa é a anexada, e se forem etiquetas diferentes o pacote sai com rastreio que não confere.">
          etiqueta:
          <select className="rounded-lg border border-gray-200 px-2 py-1 text-xs"
            value={etiqueta} onChange={e => {
              const v = e.target.value as typeof etiqueta
              setEtiqueta(v)
              if (v === 'sem-postagem' && etapa !== 'expedidos' && etapa !== 'confirmados' && etapa !== 'todos') setEtapa('expedidos')
            }}>
            <option value="">todas</option>
            <option value="com">com etiqueta</option>
            <option value="limpa">com etiqueta, sem conflito</option>
            <option value="conflito">só conflito (anexo + Melhor Envio)</option>
            <option value="sem">sem etiqueta</option>
            {/* Expedido há mais de 3 dias e a transportadora nunca registrou a
                postagem — é o pacote que pode não ter saído. Substitui "expedido
                antes da etiqueta", que acusava 3.409 num mês: a rotina é bipar e
                comprar a etiqueta depois. Só faz sentido em expedidos — a etapa
                muda junto. */}
            <option value="sem-postagem">expedido sem postagem há +3 dias (só envios pelo Melhor Envio)</option>
            {/* Duas esteiras: liberado por admin vai em onda PRÓPRIA — este filtro +
                "separar tudo" é como ela se cria. "Dispensa" junta a esteira manual
                (retirada/motoboy/externa) com os liberados: todos saem com
                declaração de retirada em vez de etiqueta. */}
            <option value="liberados">liberados sem etiqueta (onda própria)</option>
            <option value="dispensa">saem sem etiqueta (manual + liberados)</option>
          </select>
        </label>
      </div>

      {/* Canais (com contagem) — escolha para gerar a onda do canal */}
      {channelCounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setChannel('')}
            className={`rounded-full px-3 py-1 text-xs font-medium ${channel === '' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Todos ({channelCounts.reduce((s, c) => s + c.count, 0)})
          </button>
          {channelCounts.map(c => (
            <button key={c.channel} onClick={() => setChannel(c.channel === channel ? '' : c.channel)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${channel === c.channel ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              <ChannelIcon name={c.channel} /> {c.channel} ({c.count})
            </button>
          ))}
        </div>
      )}

      {/* Busca + data + tamanho */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          className="flex-1 min-w-48 rounded-lg border border-gray-200 px-3 py-2 text-sm"
          placeholder="Buscar WS, NF, nº do pedido, nº ERP ou cliente — em todo o histórico, ignora as datas"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <input type="date" className="rounded-lg border border-gray-200 px-2 py-2" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="Data inicial" />
          <span className="text-gray-400">–</span>
          <input type="date" className="rounded-lg border border-gray-200 px-2 py-2" value={dateTo} onChange={e => setDateTo(e.target.value)} title="Data final" />
        </div>
        <select
          className="rounded-lg border border-gray-200 px-2 py-2 text-sm"
          value={pageSize}
          onChange={e => setPageSize(Number(e.target.value))}
          title="Pedidos por página"
        >
          {[20, 50, 100, 500].map(n => <option key={n} value={n}>{n}/pág</option>)}
        </select>
      </div>

      {channel && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-2 text-xs text-blue-800">
          <b>Onda por canal: {channel}</b>
          {schedFor
            ? <> · corte <b>{schedFor.cutoff || '—'}</b> · expedição máx. <b>{schedFor.maxExpedition || '—'}</b></>
            : <> · <span className="text-blue-600">corte/expedição não configurados (defina em Integrações → "Corte e expedição por canal")</span></>}
          {' '}· selecione os pedidos deste canal e clique em <b>Separar</b> para gerar a onda.
        </div>
      )}

      {danfeErr && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">{danfeErr}</div>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : filtered.length === 0 && selectedList.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum pedido encontrado</p>
          ) : (
            <>
            {/* Celular: lista de cartões (o table de 11 colunas não cabe) */}
            <div className="divide-y divide-gray-100 lg:hidden">
              {canEdit && (
                <label className="flex items-center gap-2 bg-gray-50 px-4 py-2 text-xs font-medium text-gray-500">
                  <input type="checkbox" className="h-4 w-4"
                    checked={filtered.length > 0 && filtered.every(o => selected.has(o.id))}
                    onChange={e => setSelected(prev => {
                      const n = new Map(prev)
                      filtered.forEach(o => e.target.checked ? n.set(o.id, o) : n.delete(o.id))
                      return n
                    })} />
                  Selecionar todos ({filtered.length})
                </label>
              )}
              {selectedList.length > 0 && (
                <>
                  <div className="flex items-center gap-2 bg-blue-50 px-4 py-1.5 text-xs font-semibold text-blue-700">
                    Selecionados ({selectedList.length})
                    <button className="font-normal text-blue-600 underline" onClick={() => setSelected(new Map())}>limpar</button>
                  </div>
                  {selectedList.map(renderCard)}
                  <div className="border-b-2 border-blue-200" />
                </>
              )}
              {rest.map(renderCard)}
            </div>
            {/* Desktop: tabela completa */}
            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    {canEdit && (
                      <th className="px-3 py-3 text-left">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={filtered.length > 0 && filtered.every(o => selected.has(o.id))}
                          onChange={e => setSelected(prev => {
                            const n = new Map(prev)
                            filtered.forEach(o => e.target.checked ? n.set(o.id, o) : n.delete(o.id))
                            return n
                          })}
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left font-medium text-gray-600">WS</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600" title="Emissão da NF (ou data de importação, se sem NF) — é a data usada no filtro">Data</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Nº ERP</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">NF</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Cliente</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Canal</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Transportadora</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Etiqueta</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Rastreio</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">DANFE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {selectedList.length > 0 && (
                    <>
                      <tr className="bg-blue-50">
                        <td colSpan={12} className="px-4 py-1.5 text-xs font-semibold text-blue-700">
                          Selecionados ({selectedList.length})
                          <button className="ml-2 font-normal text-blue-600 underline hover:text-blue-800" onClick={() => setSelected(new Map())}>limpar</button>
                        </td>
                      </tr>
                      {selectedList.map(renderRow)}
                      <tr><td colSpan={12} className="border-b-2 border-blue-200 p-0" /></tr>
                    </>
                  )}
                  {rest.map(renderRow)}
                </tbody>
              </table>
            </div>
            </>
          )}
          {total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-3 text-sm">
              <span className="text-gray-500">
                {total} pedido(s) · <b className="text-gray-700">{pageCount}</b> página(s) de {pageSize}
              </span>
              <div className="flex items-center gap-1">
                <button disabled={page <= 1} onClick={() => setPage(1)}
                  className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-30">«</button>
                <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-30">‹ anterior</button>
                <span className="px-2 font-medium text-gray-700">{page} / {pageCount}</span>
                <button disabled={page >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                  className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-30">próxima ›</button>
                <button disabled={page >= pageCount} onClick={() => setPage(pageCount)}
                  className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-30">»</button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
